import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import Select from "react-select";
import {
  Button,
  Col,
  FormGroup,
  Label,
  Row,
  Table
} from "reactstrap";
import swal from "sweetalert";
import {
  convertDataFromSnakeCaseToCamelCase,
  convertToDateString,
  LoadingSpinner,
} from "../../../constants/const";

import moment from "moment";
import { BiSolidEdit } from "react-icons/bi";
import { BsTrash3Fill } from "react-icons/bs";
import { FiDownload } from "react-icons/fi";
import { IoArrowBackSharp, IoCash, IoCashOutline } from "react-icons/io5";
import { PiEyeDuotone } from "react-icons/pi";
import { toast, ToastContainer } from "react-toastify";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { GetLanguageString, NoResult } from "../../helper/Components";
import { GetCustomStyles, GetSelectedLanguage } from "../../helper/Method";
import DateFilterUI from "../../helper/SearchFilter/DateFilterUI";
import { ShowToast } from "../../shared/ShowToast";
import { ApiFinance } from "../ApiFinance";
import EditCashToAmanatTransaction from "./cashInHandToAmanat/EditCashToAmanatTransaction";

const ManageTransfer = ({ history }) => {
  const { t } = useTranslation();
  const [selectedTransNo, setSelectedTransNo] = useState(null);
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const [searchBtnClicked, setSearchBtnClicked] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bankAccountList, setBankAccountList] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const today = new Date();
  const [searchModel, setSearchModel] = useState({
    unitId: loggedInUnitId,
    startDate: new Date(today.getFullYear(), today.getMonth(), 1),
    endDate: new Date(),
    bankAccountId: 0,
  });
  const [transactionData, setTransactionData] = useState();

  useEffect(() => {
    const financeApi = ApiFinance();

    financeApi
      .GetFinanceBankAccounts(loggedInUnitId)
      .then((res) => {
        const filteredAccounts = (res.data || []).filter(
          (account) =>
            account?.label1 === "cash_in_hand_account" ||
            account?.label1 === "amanat_account",
        );

        const firstBank = res.data[0];

        if (filteredAccounts.length > 0) {
          setBankAccountList(filteredAccounts);
          setSelectedBankAccount(filteredAccounts[0]);

          // Update model also
          setSearchModel((prev) => ({
            ...prev,
            bankAccountId: firstBank.value, // ensure your API returns .value OR adjust accordingly
          }));
        } else {
          setBankAccountList([]);
          setSelectedBankAccount(null);

          setSearchModel((prev) => ({
            ...prev,
            bankAccountId: 0,
          }));
        }
      })
      .catch((error) => {
        setBankAccountList([]);
        setSelectedBankAccount(null);
      })
      .finally(() => { });
  }, [loggedInUnitId]);

  const handleSearch = () => {
    if (searchModel.bankAccountId !== 0) {
      setSearchBtnClicked(true);
      setLoading(true);
      ApiFinance()
        .GetCashToAmanatTransactionsByDateAndAccount({
          ...searchModel,
          startDate: convertToDateString(searchModel.startDate),
          endDate: convertToDateString(searchModel.endDate),
        })
        .then((res) => {
          // Handle response data here
          var convertedData = convertDataFromSnakeCaseToCamelCase(res.data);
          setTransactionData(convertedData);
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleOnDelete = (value) => {
    swal({
      title: t("common_delete_message"),
      text: t("common_delete_detail_message"),
      className: GetSelectedLanguage() === "en" ? "" : "ur",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        setLoading(true);
        ApiFinance()
          .DeleteTransaction(value)
          .then((res) => {
            handleSearch();
            if (res.data.statusCode === "200") {
              swal({
                title: t("transaction_deleted"),
                className: GetSelectedLanguage() === "en" ? "" : "ur",
                icon: "success",
                buttons: "OK",
              });
            } else {
              swal({
                title: t(res.data.message),
                icon: "error",
                buttons: "OK",
              });
            }
            setLoading(false);
          })
          .catch((err) => {
            console.log(err);
            swal({
              title: t(err?.message || "error_occurred"),
              icon: "error",
              buttons: "OK",
            });
            setLoading(false);
          });
      }
    });
  };

  const getAttachmentFile = (transNo) => {
    const toastId = toast.loading(t("fetching_attachment"));
    ApiFinance()
      .ViewTransactionAttachment(transNo)
      .then((res) => {
        try {
          const contentType =
            res.headers["content-type"] || "application/octet-stream";

          const blob = new Blob([res.data], { type: contentType });
          const fileURL = window.URL.createObjectURL(blob);

          // Open file in new window/tab instead of downloading
          window.open(fileURL, "_blank");

          // Optional: Clean up the object URL after a delay
          setTimeout(() => {
            window.URL.revokeObjectURL(fileURL);
          }, 100);

          ShowToast(toastId, "attachment_fetched", "success");
        } catch (error) {
          console.error("Error opening attachment:", error);
          ShowToast(toastId, "error_opening_attachment", "error");
        }
      })
      .catch((err) => {
        console.error("Error fetching attachment:", err);
        ShowToast(toastId, "error_downloading_attachment", "error");
      });
  };
  const downloadAttachmentFile = (transNo) => {
    const toastId = toast.loading(t("downloading_attachment"));
    ApiFinance()
      .ViewTransactionAttachment(transNo)
      .then((res) => {
        try {
          const contentType =
            (res.headers &&
              (res.headers["content-type"] || res.headers["Content-Type"])) ||
            "application/octet-stream";
          const disposition =
            (res.headers &&
              (res.headers["content-disposition"] ||
                res.headers["Content-Disposition"])) ||
            "";

          // Try to extract filename from content-disposition header
          let fileName = "attachment";
          const fileNameMatch = disposition.match(
            /filename\*?=(?:UTF-8'')?["']?([^;"']+)/i,
          );
          if (fileNameMatch && fileNameMatch[1]) {
            try {
              fileName = decodeURIComponent(fileNameMatch[1]);
            } catch {
              fileName = fileNameMatch[1];
            }
          }

          // Determine extension from content type if filename has no extension
          const mime = contentType.split(";")[0].trim().toLowerCase();
          const extMap = {
            "application/pdf": "pdf",
            "image/png": "png",
            "image/jpeg": "jpg",
            "image/jpg": "jpg",
            "image/gif": "gif",
          };
          const ext = extMap[mime] || "bin";
          if (!/\.[a-z0-9]+$/i.test(fileName)) {
            fileName = `${fileName}.${ext}`;
          }

          const blob = new Blob([res.data], { type: contentType });
          const fileURL = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = fileURL;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Cleanup
          setTimeout(() => {
            window.URL.revokeObjectURL(fileURL);
          }, 100);

          ShowToast(toastId, "downloaded_attachment", "success");
        } catch (error) {
          console.error("Error downloading attachment:", error);
          ShowToast(toastId, "error_downloading_attachment", "error");
        }
      })
      .catch((err) => {
        console.error("Error fetching attachment:", err);
        ShowToast(toastId, "error_downloading_attachment", "error");
      });
  };
  const formatAccount = (transaction) => {
    const CashInHand = "Cash in Hand";
    const AmanatAccount = "Amanat Account";

    if (transaction && transaction.personName && transaction.personName.includes("Transit Halqas Share to Markaz")) {
      return transaction.personName;
    }

    if (!selectedBankAccount) {
      return `${CashInHand} to ${AmanatAccount}`;
    }

    // selectedBankAccount can be either an object (from react-select) or a string.
    const label =
      typeof selectedBankAccount === "string"
        ? selectedBankAccount
        : selectedBankAccount.label ||
        selectedBankAccount.name ||
        selectedBankAccount.value ||
        "";

    const isCashToAmanat =
      label === "Cash in Hand" || label === "Cash in Hand Account";

    return isCashToAmanat
      ? `${CashInHand} to ${AmanatAccount}`
      : `${AmanatAccount} to ${CashInHand}`;
  };
  const [accountType, setAccountType] = useState("CASH");

  const resolveAccountType = (account) => {
    if (!account) return "CASH";

    const label =
      typeof account === "string"
        ? account
        : account.label || account.name || account.value || "";

    if (label === "Amanat Account") return "AMANAT";

    if (label === "Cash in Hand" || label === "Cash in Hand Account")
      return "CASH";

    return "CASH"; // safe default
  };

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <div className="ibox ">
        <ToastContainer
          autoClose={3000}
          bodyClassName={GetSelectedLanguage()}
          closeOnClick
          pauseOnHover={false}
          draggable
        />
        <div className="ibox-title">
          <h5>{<GetLanguageString props="transactions_manage_transfer" />}</h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            {" "}
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            {<GetLanguageString props="courses_participants_back" />}
          </Button>
        </div>
        <div className="ibox-content">
          <Row>
            {/* Bank Account */}
            <Col md="4">
              <FormGroup>
                <Label className="col-form-label">
                  <GetLanguageString props="fund_bank_account" />
                </Label>
                <Select
                  options={bankAccountList}
                  value={selectedBankAccount}
                  placeholder="Select bank account"
                  className="basic-single no-drag"
                  classNamePrefix="select"
                  onChange={(option) => {
                    setSelectedBankAccount(option);
                    setAccountType(resolveAccountType(option));
                    setSearchModel((prev) => ({
                      ...prev,
                      bankAccountId: option ? option.value : 0,
                    }));
                  }}
                />
              </FormGroup>
            </Col>

            <Col md="8">
              <FormGroup>
                <Col md="12">
                  <DateFilterUI
                    values={searchModel}
                    setValues={setSearchModel}
                    showFullDate={true}
                  />
                </Col>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="12" className="text-center">
              <div style={{ marginTop: 10 }}>
                <Button
                  color="primary"
                  className="m-l-sm"
                  onClick={handleSearch}
                >
                  {<GetLanguageString props="common_search" />}
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </div>
      {searchBtnClicked && (
        <>
          {!loading ? (
            <div className="ibox">
              <div className="ibox-content">
                <>
                  {openEditModal && (
                    <EditCashToAmanatTransaction
                      setModal={setOpenEditModal}
                      transNo={selectedTransNo}
                      onSuccess={handleSearch}
                    />
                  )}
                  {transactionData &&
                    transactionData.data.transactions.length === 0 ? (
                    <NoResult />
                  ) : (
                    <>
                      <style>{`
                        .balance-card-hover:hover {
                          transform: translateY(-2px);
                          box-shadow: 0 6px 12px rgba(0,0,0,0.08) !important;
                        }
                      `}</style>
                      <Row style={{ marginBottom: "15px" }}>
                        {/* Opening Balance */}
                        <Col xs="12" md="6" xl="6" className="mb-3">
                          <div
                            style={{
                              padding: "12px 20px",
                              backgroundColor: "#ffffff",
                              borderRadius: "8px",
                              border: "1px solid #e7eaec",
                              borderLeft: "5px solid #1ab394",
                              boxShadow: "0 4px 6px rgba(0,0,0,0.03)",
                              transition: "transform 0.2s, box-shadow 0.2s",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center"
                            }}
                            className="balance-card-hover"
                          >
                            <div>
                              <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#888888", fontWeight: "600", letterSpacing: "0.5px" }}>
                                <GetLanguageString props="opening_balance" />
                              </div>
                              <div style={{ fontSize: "24px", fontWeight: "700", marginTop: "4px", color: "#1ab394" }}>
                                Rs {new Intl.NumberFormat().format(transactionData.data.openingBalance)}
                              </div>
                              <div style={{ fontSize: "12px", color: "#888888", marginTop: "4px" }}>
                                <GetLanguageString props="member_member_detail_common_responsibility_from_date" />{" "}
                                {moment(transactionData.data.fromDate, "DD-MM-YYYY").format("DD/MM/YYYY")}
                              </div>
                            </div>
                            <div style={{ backgroundColor: "#e8f7f4", padding: "10px", borderRadius: "50%" }}>
                              <IoCashOutline style={{ color: "#1ab394", fontSize: "26px" }} />
                            </div>
                          </div>
                        </Col>

                        {/* Closing Balance */}
                        <Col xs="12" md="6" xl="6" className="mb-3">
                          <div
                            style={{
                              padding: "12px 20px",
                              backgroundColor: "#ffffff",
                              borderRadius: "8px",
                              border: "1px solid #e7eaec",
                              borderLeft: "5px solid #3b82f6",
                              boxShadow: "0 4px 6px rgba(0,0,0,0.03)",
                              transition: "transform 0.2s, box-shadow 0.2s",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center"
                            }}
                            className="balance-card-hover"
                          >
                            <div>
                              <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#888888", fontWeight: "600", letterSpacing: "0.5px" }}>
                                <GetLanguageString props="closing_balance" />
                              </div>
                              <div style={{ fontSize: "24px", fontWeight: "700", marginTop: "4px", color: "#3b82f6" }}>
                                Rs {new Intl.NumberFormat().format(transactionData.data.closingBalance)}
                              </div>
                              <div style={{ fontSize: "12px", color: "#888888", marginTop: "4px" }}>
                                <GetLanguageString props="member_member_detail_common_responsibility_to_date" />{" "}
                                {moment(transactionData.data.toDate, "DD-MM-YYYY").format("DD/MM/YYYY")}
                              </div>
                            </div>
                            <div style={{ backgroundColor: "#eff6ff", padding: "10px", borderRadius: "50%" }}>
                              <IoCash style={{ color: "#3b82f6", fontSize: "24px" }} />
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <Table>
                        <thead>
                          <tr style={{ borderBottom: "1px solid black" }}>
                            <th className="text-center">#</th>
                            <th className="text-center">
                              {<GetLanguageString props="funds_date" />}
                            </th>
                            <th className="text-center">
                              {<GetLanguageString props="common_type" />}
                            </th>

                            <th className="text-center">
                              {
                                <GetLanguageString props="funds_add_fund_amount" />
                              }
                            </th>
                            <th className="text-center">
                              {
                                <GetLanguageString props="transactions_balance" />
                              }
                            </th>

                            <th className="text-center">
                              {<GetLanguageString props="common_action" />}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactionData.data.transactions.map(
                            (transaction, index) => (
                              <tr key={index}>
                                <td className="text-center">{index + 1}</td>
                                <td className="text-center">
                                  {moment(
                                    transaction.date,
                                    "YYYY-MM-DD",
                                  ).format("DD/MM/YYYY")}
                                </td>
                                <td className="text-center">
                                  {formatAccount(transaction)}
                                </td>

                                <td
                                  className={"text-center "}
                                  style={{
                                    color:
                                      transaction.amount < 0 ? "red" : "green",
                                  }}
                                >
                                  <strong>
                                    {new Intl.NumberFormat().format(
                                      transaction.amount,
                                    )}
                                  </strong>
                                </td>

                                <td className="text-center">
                                  {new Intl.NumberFormat().format(
                                    transaction.runningBalance,
                                  )}
                                </td>

                                <td className="text-center">
                                  {!(transaction.personName && transaction.personName.includes("Transit Halqas Share to Markaz")) && (
                                    <div>
                                      <PiEyeDuotone
                                        color="#cf871bff"
                                        value={transaction.transNo}
                                        onClick={() =>
                                          getAttachmentFile(transaction.transNo)
                                        }
                                        style={{
                                          outline: "none",
                                          cursor: "pointer",
                                        }}
                                        size={20}
                                      />
                                      &nbsp;&nbsp;&nbsp;
                                      <FiDownload
                                        color="#35A29F"
                                        value={transaction.transNo}
                                        onClick={() =>
                                          downloadAttachmentFile(
                                            transaction.transNo,
                                          )
                                        }
                                        style={{
                                          outline: "none",
                                          cursor: "pointer",
                                        }}
                                        size={20}
                                      />
                                      &nbsp;&nbsp;&nbsp;
                                      {accountType !== "AMANAT" && (
                                        <>
                                          <BiSolidEdit
                                            color="#3588a2ff"
                                            style={{
                                              outline: "none",
                                              cursor: "pointer",
                                            }}
                                            size={20}
                                            onClick={() => {
                                              setSelectedTransNo(
                                                transaction.transNo,
                                              ); // save selected trans_no
                                              setOpenEditModal(true); // open modal
                                            }}
                                          />
                                          &nbsp;&nbsp;&nbsp;
                                          <BsTrash3Fill
                                            color="#bd3826ff"
                                            style={{
                                              outline: "none",
                                              cursor: "pointer",
                                            }}
                                            size={20}
                                            value={transaction.transNo}
                                            onClick={() =>
                                              handleOnDelete(transaction.transNo)
                                            }
                                          />
                                        </>
                                      )}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </Table>
                    </>
                  )}
                </>
              </div>
            </div>
          ) : (
            LoadingSpinner()
          )}
        </>
      )}
    </div>
  );
};

export default withRouter(ManageTransfer);
